// Table sheet (29 Sep 2026). A reply table stays a normal table in the chat;
// tapping it opens the same sheet as Helpers / Cost Breakdown with the table
// larger and calmer: sticky header, sticky first column, and a tapped row stays
// highlighted so the eye never loses its line (Julian reads on a phone and is
// dyslexic). Cell HTML is the reply's already-sanitized markup (DOMPurify in
// Markdown.tsx); nothing new is parsed here.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "react-feather";

export interface TableData { title: string; headers: string[]; labels: string[]; rows: string[][]; numeric: boolean[]; long: boolean[] }

/** The heading or bold lead-in right above the table names it; otherwise "Table". */
function titleFor(box: Element): string {
  let node = box.previousElementSibling;
  for (let hops = 0; node && hops < 2; hops++, node = node.previousElementSibling) {
    const text = (node.textContent ?? "").replace(/\s+/g, " ").trim().replace(/[:：]$/, "");
    if (/^H[1-6]$/.test(node.tagName) || (node.tagName === "P" && node.querySelector("strong") && text.length <= 80)) {
      if (text) return text;
    }
  }
  return "Table";
}

export function readTable(box: Element): TableData | null {
  const table = box.querySelector("table");
  if (!table) return null;
  const heads = Array.from(table.querySelectorAll("thead th"));
  const bodyRows = Array.from(table.querySelectorAll("tbody tr")).map((tr) => Array.from(tr.querySelectorAll("td")));
  return {
    title: titleFor(box),
    // Long-text columns get a wider measure, so rows stay short and read as sentences.
    long: heads.map((_, c) => bodyRows.some((cells) => (cells[c]?.textContent ?? "").length > 28)),
    headers: heads.map((th) => th.innerHTML),
    labels: heads.map((th) => (th.textContent ?? "").replace(/\s+/g, " ").trim()),
    numeric: heads.map((th) => th.classList.contains("is-num")),
    rows: bodyRows.map((cells) => cells.map((td) => td.innerHTML)),
  };
}

function cellClass(data: TableData, c: number) {
  return [data.numeric[c] && "is-num", data.long[c] && "is-long"].filter(Boolean).join(" ") || undefined;
}

export default function TableSheet({ data, onClose }: { data: TableData; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [picked, setPicked] = useState<number | null>(null);

  useEffect(() => {
    const panel = dialog.current;
    if (!panel) return;
    const opener = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!panel.open) panel.showModal();
    panel.querySelector<HTMLElement>(".helpersSheet__panel")?.focus({ preventScroll: true });
    return () => {
      if (panel.open) panel.close();
      document.body.style.overflow = overflow;
      opener?.focus?.({ preventScroll: true });
    };
  }, []);

  const cols = data.headers.length;
  const rows = data.rows.length;
  return createPortal(
    <dialog
      ref={dialog}
      className="helpersSheet tableSheet"
      aria-labelledby="tableSheet-title"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="helpersSheet__panel" tabIndex={-1}>
        <div className="helpersSheet__grabber" aria-hidden="true" />
        <div className="helpersSheet__view">
          <header className="helpersSheet__head">
            <div className="helpersSheet__titles">
              <h2 id="tableSheet-title">{data.title}</h2>
              <p>{rows} {rows === 1 ? "row" : "rows"} · {cols} {cols === 1 ? "column" : "columns"}</p>
            </div>
            <button type="button" className="helpersSheet__icon" aria-label="Close table" onClick={onClose}>
              <X width={20} height={20} aria-hidden="true" />
            </button>
          </header>
          <div className="helpersSheet__body tableSheet__body">
            <div className={`tableSheet__scroll${cols >= 3 ? " has-lead" : ""}`} tabIndex={0} role="region" aria-label={data.title}
              onKeyDown={(event) => {
                // Arrow keys move the highlighted row, like a reading ruler.
                if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
                event.preventDefault();
                const step = event.key === "ArrowDown" ? 1 : -1;
                const next = Math.min(rows - 1, Math.max(0, picked === null ? 0 : picked + step));
                setPicked(next);
                event.currentTarget.querySelectorAll("tbody tr")[next]?.scrollIntoView({ block: "nearest" });
              }}>
              <table className="tableSheet__table">
                <thead><tr>{data.headers.map((h, c) =>
                  <th key={c} className={cellClass(data, c)} dangerouslySetInnerHTML={{ __html: h }} />)}</tr></thead>
                <tbody>
                  {data.rows.map((row, r) => (
                    <tr key={r} className={picked === r ? "is-picked" : undefined} aria-selected={picked === r}
                      onClick={() => setPicked(picked === r ? null : r)}>
                      {row.map((cell, c) =>
                        <td key={c} data-label={data.labels[c]} className={cellClass(data, c)} dangerouslySetInnerHTML={{ __html: cell }} />)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </dialog>,
    document.body,
  );
}
